import { Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { Platform } from '@ionic/angular';

export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp?: number;
}

export interface AddressData {
  city?: string;
  state?: string;
  country?: string;
  fullAddress?: string;
}

export type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unknown';

@Injectable({
  providedIn: 'root'
})
export class GeoLocationService {
  async checkLocationPermission(): Promise<{ status: PermissionStatus; error?: string }> {
    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile platform - use Capacitor Geolocation
        const permission = await Geolocation.checkPermissions();
        const status = permission.location;
        
        if (status === 'granted') {
          return { status };
        } else if (status === 'denied') {
          return { status, error: 'widgets.location.permissionHelp' };
        } else {
          return { status, error: 'widgets.location.permissionRequired' };
        }
      } else {
        // Web platform - check if geolocation is supported
        if (!navigator.geolocation) {
          return { status: 'unknown', error: 'widgets.location.notSupported' };
        }
        
        // For web, we need to try to get location to determine permission status
        try {
          // Try to get location with a very short timeout to check permission
          const position = await this.getWebLocationWithTimeout(100);
          return { status: 'granted' };
        } catch (error) {
          if (error instanceof GeolocationPositionError) {
            switch (error.code) {
              case error.PERMISSION_DENIED:
                return { status: 'denied', error: 'widgets.location.permissionHelp' };
              case error.POSITION_UNAVAILABLE:
                return { status: 'unknown', error: 'widgets.location.unavailable' };
              case error.TIMEOUT:
                // Timeout might mean permission is not granted yet
                return { status: 'prompt', error: 'widgets.location.permissionRequired' };
              default:
                return { status: 'unknown', error: 'widgets.location.error' };
            }
          }
          return { status: 'prompt', error: 'widgets.location.permissionRequired' };
        }
      }
    } catch (error) {
      console.error('Error checking location permission:', error);
      return { status: 'unknown', error: 'widgets.location.error' };
    }
  }

  async requestLocationPermission(): Promise<{ status: PermissionStatus; error?: string }> {
    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile platform - use Capacitor Geolocation
        const permission = await Geolocation.requestPermissions();
        const status = permission.location;
        
        if (status === 'granted') {
          return { status };
        } else {
          return { status, error: 'widgets.location.permissionDenied' };
        }
      } else {
        // Web platform - try to get location which will trigger permission request
        try {
          const position = await this.getWebLocation();
          return { status: 'granted' };
        } catch (error) {
          if (error instanceof GeolocationPositionError) {
            switch (error.code) {
              case error.PERMISSION_DENIED:
                return { status: 'denied', error: 'widgets.location.permissionDenied' };
              case error.POSITION_UNAVAILABLE:
                return { status: 'unknown', error: 'widgets.location.unavailable' };
              case error.TIMEOUT:
                return { status: 'unknown', error: 'widgets.location.timeout' };
              default:
                return { status: 'unknown', error: 'widgets.location.error' };
            }
          }
          return { status: 'unknown', error: 'widgets.location.error' };
        }
      }
    } catch (error) {
      return { status: 'unknown', error: 'widgets.location.permissionHelp' };
    }
  }

  async getCurrentLocation(): Promise<{ location?: LocationData; error?: string }> {
    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile platform - use Capacitor Geolocation
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        });

        const location: LocationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };

        return { location };
      } else {
        // Web platform - use Web Geolocation API
        const position = await this.getWebLocation();
        
        const location: LocationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };

        return { location };
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            return { error: 'widgets.location.permissionDenied' };
          case error.POSITION_UNAVAILABLE:
            return { error: 'widgets.location.unavailable' };
          case error.TIMEOUT:
            return { error: 'widgets.location.timeout' };
          default:
            return { error: 'widgets.location.error' };
        }
      } else {
        return { error: 'widgets.location.error' };
      }
    }
  }

  private getWebLocation(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position);
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }

  private getWebLocationWithTimeout(timeoutMs: number): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position);
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: timeoutMs,
          maximumAge: 0 
        }
      );
    });
  }

  getMapUrl(location: LocationData): string {
    if (!location) return '';
    // Using static Google Maps URL (no API key required)
    return `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`;
  }

  getCoordinatesText(location: LocationData): string {
    if (!location) return '';
    return `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;
  }

  async reverseGeocode(location: LocationData): Promise<{ address?: AddressData; error?: string }> {
    try {
      if (!location) {
        return { error: 'No location data provided' };
      }

      // Using OpenStreetMap Nominatim API (free, no API key required)
        const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.latitude}&lon=${location.longitude}&addressdetails=1`
      );

        if (!response.ok) {
          return { error: 'Failed to fetch address data' };
        }

        const data = await response.json();
        
        if (data.error) {
          return { error: data.error };
        }

        // Expanded fallback logic for city name - check multiple possible fields
        // OpenStreetMap may return different fields depending on location (e.g., Dubai might use municipality, county, etc.)
        const cityName = data.address?.city || 
                        data.address?.town || 
                        data.address?.village || 
                        data.address?.hamlet ||
                        data.address?.municipality ||
                        data.address?.county ||
                        data.address?.state_district ||
                        data.address?.region ||
                        data.address?.suburb ||
                        data.address?.neighbourhood ||
                        null;

        const address: AddressData = {
          city: cityName,
          state: data.address?.state,
          country: data.address?.country,
          fullAddress: data.display_name
        };

        return { address };
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return { error: 'Failed to get address information' };
    }
  }

  async copyCoordinates(location: LocationData): Promise<void> {
    if (!location) {
      return;
    }

    const coordinates = this.getCoordinatesText(location);
    
    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile platform - try to use clipboard API if available
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(coordinates);
        } else {
          // Fallback for older mobile browsers
          this.fallbackCopyToClipboard(coordinates);
        }
      } else {
        // Web platform - use Web Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(coordinates);
        } else {
          // Fallback for older browsers
          this.fallbackCopyToClipboard(coordinates);
        }
      }
    } catch (error) {
      console.error('Error copying coordinates:', error);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback copy failed:', err);
      throw err;
    } finally {
      document.body.removeChild(textArea);
    }
  }

  async openLocationSettings() {
    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails, 
      optionIOS: IOSSettings.App
    })
  }

  /**
   * Smart location update that only updates if location-dependent widgets are visible
   * This method should be called from AppComponent when the app resumes
   */
  async updateLocationIfNeeded(): Promise<void> {
    try {
      // Check if location or weather widgets are visible
      const widgetsOrderAndVisibility = localStorage.getItem('widgets-order-and-visibility');
      if (!widgetsOrderAndVisibility) {
        return; // No widgets configured, skip location update
      }

      const widgets = JSON.parse(widgetsOrderAndVisibility);
      const locationWidgetVisible = widgets.find((w: any) => w.key === 'location' && w.visible);
      const weatherWidgetVisible = widgets.find((w: any) => w.key === 'weather' && w.visible);

      // Only update location if at least one location-dependent widget is visible
      if (locationWidgetVisible || weatherWidgetVisible) {
        const permissionResult = await this.checkLocationPermission();
        if (permissionResult.status === 'granted') {
          // Trigger location update by getting current location
          // This will automatically update any visible widgets that depend on location
          await this.getCurrentLocation();
        }
      }
    } catch (error) {
      console.error('Error in smart location update:', error);
    }
  }
}
